import { AdminLayout } from "./AdminLayout";
import { Plus, Search, MoreVertical, Eye, Edit, Trash2, FileText } from "lucide-react";
import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../../App";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../../components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../../components/ui/dropdown-menu";

const Blog = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [formData, setFormData] = useState({
    title: '', slug: '', excerpt: '', content: '', featured_image: '', status: 'draft', tags: ''
  });

  const fetchPosts = async () => {
    try {
      const params = statusFilter ? { status: statusFilter } : {};
      const response = await axios.get(`${API}/blog/posts`, { params, withCredentials: true });
      setPosts(response.data);
    } catch (error) {
      toast.error("Failed to fetch posts");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchPosts(); }, [statusFilter]);

  const generateSlug = (title) => {
    return title.toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  const handleTitleChange = (e) => {
    const title = e.target.value;
    setFormData({
      ...formData,
      title,
      slug: editingPost ? formData.slug : generateSlug(title)
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        tags: formData.tags ? formData.tags.split(',').map(t => t.trim()).filter(t => t) : []
      };
      if (editingPost) {
        await axios.put(`${API}/blog/posts/${editingPost.post_id}`, data, { withCredentials: true });
        toast.success("Post updated");
      } else {
        await axios.post(`${API}/blog/posts`, data, { withCredentials: true });
        toast.success("Post created");
      }
      setIsDialogOpen(false);
      setEditingPost(null);
      setFormData({ title: '', slug: '', excerpt: '', content: '', featured_image: '', status: 'draft', tags: '' });
      fetchPosts();
    } catch (error) {
      toast.error("Failed to save post");
    }
  };

  const handleEdit = (post) => {
    setEditingPost(post);
    setFormData({
      title: post.title, slug: post.slug, excerpt: post.excerpt || '',
      content: post.content, featured_image: post.featured_image || '',
      status: post.status, tags: post.tags?.join(', ') || ''
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (postId) => {
    if (!window.confirm('Are you sure you want to delete this post?')) return;
    try {
      await axios.delete(`${API}/blog/posts/${postId}`, { withCredentials: true });
      toast.success("Post deleted");
      fetchPosts();
    } catch (error) {
      toast.error("Failed to delete post");
    }
  };

  const updateStatus = async (postId, status) => {
    try {
      await axios.put(`${API}/blog/posts/${postId}`, { status }, { withCredentials: true });
      toast.success(`Post ${status === 'published' ? 'published' : 'updated'}`);
      fetchPosts();
    } catch (error) {
      toast.error("Failed to update post");
    }
  };

  const statusColors = {
    draft: 'bg-[#8A8A8A]/20 text-[#8A8A8A]',
    published: 'bg-green-500/20 text-green-400',
    archived: 'bg-yellow-500/20 text-yellow-400'
  };

  return (
    <AdminLayout title="Content">
      <div className="space-y-6" data-testid="admin-blog">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h2 className="text-xl font-bold">Blog Posts</h2>
            <p className="text-sm text-[#8A8A8A]">{posts.length} total</p>
          </div>
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <button className="btn-primary text-sm flex items-center gap-2"
                onClick={() => { setEditingPost(null); setFormData({ title: '', slug: '', excerpt: '', content: '', featured_image: '', status: 'draft', tags: '' }); }}
                data-testid="add-post-btn">
                <Plus className="w-4 h-4" /> New Post
              </button>
            </DialogTrigger>
            <DialogContent className="bg-[#1C1C1C] border-[#2E2E2E] max-w-3xl max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle>{editingPost ? 'Edit Post' : 'New Blog Post'}</DialogTitle></DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <input type="text" placeholder="Post Title *" required value={formData.title}
                  onChange={handleTitleChange} className="input-field" data-testid="post-title-input" />
                <div className="grid grid-cols-2 gap-4">
                  <input type="text" placeholder="URL Slug *" required value={formData.slug}
                    onChange={(e) => setFormData({...formData, slug: e.target.value})} className="input-field" />
                  <select value={formData.status} onChange={(e) => setFormData({...formData, status: e.target.value})} className="input-field">
                    <option value="draft">Draft</option>
                    <option value="published">Published</option>
                    <option value="archived">Archived</option>
                  </select>
                </div>
                <input type="url" placeholder="Featured Image URL" value={formData.featured_image}
                  onChange={(e) => setFormData({...formData, featured_image: e.target.value})} className="input-field" />
                <textarea placeholder="Excerpt (short description)" rows={2} value={formData.excerpt}
                  onChange={(e) => setFormData({...formData, excerpt: e.target.value})} className="input-field resize-none" />
                <textarea placeholder="Content (HTML supported) *" required rows={12} value={formData.content}
                  onChange={(e) => setFormData({...formData, content: e.target.value})} className="input-field resize-none font-mono text-sm" />
                <input type="text" placeholder="Tags (comma separated)" value={formData.tags}
                  onChange={(e) => setFormData({...formData, tags: e.target.value})} className="input-field" />
                <div className="flex justify-end gap-3">
                  <button type="button" onClick={() => setIsDialogOpen(false)} className="btn-secondary text-sm">Cancel</button>
                  <button type="submit" className="btn-primary text-sm" data-testid="save-post-btn">
                    {editingPost ? 'Update' : 'Create'} Post
                  </button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Filters */}
        <div className="flex gap-4">
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="input-field w-48">
            <option value="">All Statuses</option>
            <option value="draft">Draft</option>
            <option value="published">Published</option>
            <option value="archived">Archived</option>
          </select>
        </div>

        {/* Posts Table */}
        <div className="border border-[#2E2E2E] overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-[#1C1C1C] border-b border-[#2E2E2E]">
                <tr>
                  <th className="text-left p-4 text-sm font-medium text-[#8A8A8A]">Post</th>
                  <th className="text-left p-4 text-sm font-medium text-[#8A8A8A]">Status</th>
                  <th className="text-left p-4 text-sm font-medium text-[#8A8A8A]">Tags</th>
                  <th className="text-left p-4 text-sm font-medium text-[#8A8A8A]">Date</th>
                  <th className="p-4"></th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  [...Array(3)].map((_, i) => (
                    <tr key={i} className="border-b border-[#2E2E2E]">
                      <td colSpan={5} className="p-4"><div className="h-4 bg-[#2E2E2E] rounded animate-pulse" /></td>
                    </tr>
                  ))
                ) : posts.length === 0 ? (
                  <tr><td colSpan={5} className="p-8 text-center text-[#8A8A8A]">No posts found</td></tr>
                ) : (
                  posts.map((post) => (
                    <tr key={post.post_id} className="border-b border-[#2E2E2E] hover:bg-[#1C1C1C]" data-testid={`post-row-${post.post_id}`}>
                      <td className="p-4">
                        <div className="flex items-start gap-4">
                          {post.featured_image ? (
                            <img src={post.featured_image} alt="" className="w-16 h-10 object-cover border border-[#2E2E2E]" />
                          ) : (
                            <div className="w-16 h-10 bg-[#2E2E2E] flex items-center justify-center">
                              <FileText className="w-4 h-4 text-[#8A8A8A]" />
                            </div>
                          )}
                          <div>
                            <div className="font-medium">{post.title}</div>
                            <div className="text-xs text-[#8A8A8A]">/{post.slug}</div>
                          </div>
                        </div>
                      </td>
                      <td className="p-4">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button className={`badge ${statusColors[post.status]} cursor-pointer capitalize`}>{post.status}</button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent className="bg-[#1C1C1C] border-[#2E2E2E]">
                            {['draft', 'published', 'archived'].map((status) => (
                              <DropdownMenuItem key={status} onClick={() => updateStatus(post.post_id, status)}
                                className="capitalize cursor-pointer hover:bg-[#2E2E2E]">{status}</DropdownMenuItem>
                            ))}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                      <td className="p-4">
                        <div className="flex flex-wrap gap-1">
                          {(post.tags || []).slice(0, 2).map((tag, i) => (
                            <span key={i} className="text-xs bg-[#2E2E2E] px-2 py-0.5">{tag}</span>
                          ))}
                          {(post.tags || []).length > 2 && (
                            <span className="text-xs text-[#8A8A8A]">+{post.tags.length - 2}</span>
                          )}
                        </div>
                      </td>
                      <td className="p-4 text-sm text-[#8A8A8A]">
                        {post.published_at 
                          ? new Date(post.published_at).toLocaleDateString('en-GB')
                          : new Date(post.created_at).toLocaleDateString('en-GB')
                        }
                      </td>
                      <td className="p-4">
                        <div className="flex items-center gap-2">
                          {post.status === 'published' && (
                            <a href={`/blog/${post.slug}`} target="_blank" rel="noopener noreferrer" 
                              className="p-1 hover:bg-[#2E2E2E] text-[#8A8A8A]">
                              <Eye className="w-4 h-4" />
                            </a>
                          )}
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <button className="p-1 hover:bg-[#2E2E2E]"><MoreVertical className="w-4 h-4" /></button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent className="bg-[#1C1C1C] border-[#2E2E2E]">
                              <DropdownMenuItem onClick={() => handleEdit(post)} className="cursor-pointer hover:bg-[#2E2E2E]">
                                <Edit className="w-4 h-4 mr-2" /> Edit
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleDelete(post.post_id)} className="cursor-pointer hover:bg-[#2E2E2E] text-red-400">
                                <Trash2 className="w-4 h-4 mr-2" /> Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default Blog;
